/**
 * Rigorous validation rules for the required personal-data base fields
 * (first name, last name, email, mobile phone) of the shared customer-data
 * step (`PersonalDataStepShared`, #868).
 *
 * Product decision (CEO): mandatory personal-data fields are mandatory for a
 * reason — proper data is enforced rigorously for ALL use cases, not just
 * parcel. These rules are therefore UNCONDITIONAL on the four base fields
 * wherever they are required: they are part of the validation layer consumed
 * by `validatePersonalData` (bookingCreation slice), NOT per-flow prop gates.
 * This composes with the tenant-configurable `requiredFields` design
 * (#1401 / ADR-0005): "required ⇒ rigorously validated" is the invariant.
 *
 * Background: the Vonovia parcel beta received obvious dummy data — surname
 * "B", disposable address kolula@byom.de, keyboard-walk phone numbers like
 * "+49 1231 2312312" (docs/reviews/paketlieferung-delete-otp-audit.md, item 11).
 * Every observed bad input must fail these rules; legitimate short/foreign
 * names ("Li", "Bo") and diacritics/hyphens/apostrophes must pass.
 *
 * The same rules exist server-side for the unauthenticated parcel endpoint in
 * `backend/src/core/utils/personalDataRules.ts` — keep the two in sync.
 */

/**
 * Known disposable / throwaway email domains, matched exactly or as a
 * subdomain suffix. Deliberately a small in-repo constant list — no external
 * verification API (third-party-free rule). Extend as new ones are observed.
 */
export const DISPOSABLE_EMAIL_DOMAINS: readonly string[] = [
  "byom.de",
  "mailinator.com",
  "trashmail.com",
  "trashmail.de",
  "trash-mail.com",
  "10minutemail.com",
  "10minutemail.de",
  "guerrillamail.com",
  "guerrillamail.net",
  "guerrillamail.org",
  "sharklasers.com",
  "yopmail.com",
  "yopmail.fr",
  "temp-mail.org",
  "temp-mail.io",
  "tempmail.de",
  "tempmailo.com",
  "maildrop.cc",
  "getnada.com",
  "dispostable.com",
  "mohmal.com",
  "discard.email",
  "wegwerfmail.de",
  "wegwerfmail.net",
  "throwawaymail.com",
  "mailnesia.com",
  "spamgourmet.com",
  "mytemp.email",
  "fakemail.net",
  "emailondeck.com",
];

/** True when the address' domain is a known disposable-email domain. */
export const isDisposableEmail = (email: string): boolean => {
  const atIndex = email.lastIndexOf("@");
  if (atIndex < 0) return false;
  const domain = email.slice(atIndex + 1).trim().toLowerCase();
  if (!domain) return false;
  return DISPOSABLE_EMAIL_DOMAINS.some(
    (d) => domain === d || domain.endsWith(`.${d}`)
  );
};

/**
 * Obvious dummy words we reject as a first or last name. Exact match on the
 * whole (letters-only, lowercased) input or on every word of it — never a
 * substring match, so real names that merely contain one of these (e.g.
 * "Testa") are unaffected. Keyboard walks are covered for both QWERTY and
 * QWERTZ layouts.
 */
const NAME_DUMMY_WORDS: ReadonlySet<string> = new Set([
  "test",
  "tester",
  "testing",
  "testtest",
  "asdf",
  "asdfg",
  "asdfgh",
  "asdfghj",
  "asdfghjk",
  "asdfghjkl",
  "qwer",
  "qwert",
  "qwerty",
  "qwertz",
  "yxcv",
  "yxcvb",
  "abc",
  "abcd",
  "abcde",
  "foo",
  "foobar",
  "dummy",
  "bla",
  "blabla",
  "lorem",
  "ipsum",
  "keine",
  "none",
  "nix",
  "egal",
  "anonym",
  "anonymous",
  "unbekannt",
  "unknown",
  "firstname",
  "lastname",
  "vorname",
  "nachname",
  "name",
]);

/**
 * Characters allowed in a name: unicode letters (incl. combining marks for
 * diacritics), whitespace, hyphens, apostrophes and periods (abbreviated
 * middle names). Digits and other symbols are rejected.
 */
const NAME_ALLOWED_CHARS = /^[\p{L}\p{M}\s'’`´.-]+$/u;

/**
 * Plausibility check for a required first/last name.
 *
 * Accepts: >= 2 letters after trim, letters/diacritics/hyphens/apostrophes
 * only ("Li", "Bo", "José", "O'Brien", "Müller-Lüdenscheid" are all valid —
 * the minimum is 2 letters, not 3).
 * Rejects: empty / single letter ("B"), digits ("Melanie123"), all-one-letter
 * strings ("xx", "aaaa"), and obvious dummy words (test/asdf/qwer/... above).
 */
export const isPlausibleName = (name: string): boolean => {
  const trimmed = name.trim();
  if (!trimmed) return false;
  if (!NAME_ALLOWED_CHARS.test(trimmed)) return false;

  const letters = trimmed.match(/\p{L}/gu) ?? [];
  if (letters.length < 2) return false;

  // All-one-letter input ("xx", "XXX", "aaaa") is never a real name.
  const lowered = letters.join("").toLowerCase();
  if (/^(.)\1+$/u.test(lowered)) return false;

  // Whole input, or every single word of it, is a known dummy word.
  const words = trimmed
    .toLowerCase()
    .split(/[\s.'’`´-]+/)
    .filter(Boolean);
  if (NAME_DUMMY_WORDS.has(lowered)) return false;
  if (words.length > 0 && words.every((w) => NAME_DUMMY_WORDS.has(w))) {
    return false;
  }

  return true;
};

/**
 * True when a digit string is an obvious keyboard/dummy pattern: every digit
 * the same ("00000000"), or the WHOLE string is a repetition of a 2- or
 * 3-digit cycle ("909090…", "123123123…", "9089089089"). Only full-string
 * cycles are rejected so real numbers containing a repeated fragment
 * ("…7272") stay valid.
 */
export const hasImplausibleDigitPattern = (digits: string): boolean => {
  if (digits.length < 2) return false;
  if (/^(\d)\1+$/.test(digits)) return true;
  for (const cycleLength of [2, 3]) {
    if (digits.length >= cycleLength * 3) {
      const cycle = digits.slice(0, cycleLength);
      const tiled = cycle
        .repeat(Math.ceil(digits.length / cycleLength))
        .slice(0, digits.length);
      if (tiled === digits) return true;
    }
  }
  return false;
};
